import { useLocation, useNavigate } from 'react-router-dom';
import { useUIStore } from '../store';

/**
 * Navigation state passed between pages for contextual back navigation
 */
interface NavigationState {
  from: string;
  fromLabel: string;
}

/**
 * Back navigation info returned by getBackNavigation
 */
interface BackNavigation {
  path: string;
  label: string;
}

/**
 * Hook for contextual navigation with "back" link support.
 *
 * This hook enables pages to pass context about where the user came from,
 * allowing destination pages to show contextual back links like
 * "Back to Workflow" instead of hardcoded "Back to Queues".
 *
 * Features:
 * 1. Router state: Explicit navigation context (highest priority)
 * 2. Global state: Last visited workflow (for sidebar navigation)
 *
 * @example
 * // Source page (navigating away)
 * const { navigateWithContext } = useContextualNavigation();
 * navigateWithContext('/projects/1/queues/5', 'Workflow');
 *
 * // Destination page (back link with explicit context)
 * const { getBackNavigation } = useContextualNavigation();
 * const backNav = getBackNavigation('/projects/1/queues', 'Back to Queues');
 *
 * // Destination page (optional back to orchestration)
 * const { getBackToWorkflow } = useContextualNavigation();
 * const orchNav = getBackToWorkflow('1'); // projectId
 * // Returns { path, label } if workflow was recently visited, null otherwise
 */
export function useContextualNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const lastWorkflow = useUIStore((state) => state.lastWorkflow);

  /**
   * Navigate to a path while preserving context about where user came from.
   *
   * @param path - The destination path to navigate to
   * @param fromLabel - Label describing the current page (e.g., 'Workflow', 'Agent')
   * @param options - Optional navigation options (replace history entry)
   */
  const navigateWithContext = (
    path: string,
    fromLabel: string,
    options?: { replace?: boolean }
  ) => {
    navigate(path, {
      state: { from: location.pathname, fromLabel } as NavigationState,
      replace: options?.replace,
    });
  };

  /**
   * Get back navigation info - uses router state if available, falls back to default.
   *
   * This gracefully degrades: if the user navigated directly to the page (no state),
   * refreshed the page (state lost), or came from an unexpected source, the default
   * path and label are used.
   *
   * @param defaultPath - Fallback path if no navigation state exists
   * @param defaultLabel - Fallback label if no navigation state exists
   * @returns Object with path and label for the back link
   */
  const getBackNavigation = (
    defaultPath: string,
    defaultLabel: string
  ): BackNavigation => {
    const state = location.state as NavigationState | null;

    if (state?.from && state?.fromLabel) {
      // When the source is the workflow editor, prefer the persisted workflow
      // name over the generic "Workflow" label so the back link reads
      // "Back to <Workflow Name>".
      const resolvedLabel =
        state.fromLabel === 'Workflow' &&
        lastWorkflow &&
        state.from.includes(`/workflows/${lastWorkflow.workflowId}`)
          ? lastWorkflow.name
          : state.fromLabel;
      return {
        path: state.from,
        label: `Back to ${resolvedLabel}`,
      };
    }

    return {
      path: defaultPath,
      label: defaultLabel,
    };
  };

  /**
   * Get back to workflow navigation if one was recently visited.
   *
   * This checks:
   * 1. Router state (explicit navigation from orchestration)
   * 2. Global state (last visited workflow in this project)
   *
   * @param currentProjectId - The current project ID (only show if in same project)
   * @returns Navigation info if workflow was visited, null otherwise
   */
  const getBackToWorkflow = (
    currentProjectId: string | number | undefined
  ): BackNavigation | null => {
    // First check router state. Prefer the persisted workflow name when the
    // IDs match so the back link shows the actual workflow name instead of the
    // generic "Workflow" placeholder passed via fromLabel.
    const state = location.state as NavigationState | null;
    if (state?.from && state?.fromLabel === 'Workflow') {
      const nameFromStore =
        lastWorkflow && state.from.includes(`/workflows/${lastWorkflow.workflowId}`)
          ? lastWorkflow.name
          : null;
      return {
        path: state.from,
        label: `Back to ${nameFromStore || 'Workflow'}`,
      };
    }

    // Then check global state (only if in same project)
    // Use String() comparison to handle both string and number project IDs
    if (
      lastWorkflow &&
      currentProjectId &&
      String(lastWorkflow.projectId) === String(currentProjectId)
    ) {
      return {
        path: `/projects/${lastWorkflow.projectId}/workflows/${lastWorkflow.workflowId}`,
        label: `Back to ${lastWorkflow.name}`,
      };
    }

    return null;
  };

  return { navigateWithContext, getBackNavigation, getBackToWorkflow };
}
